"use client";

/**
 * FE-3 — Review workspace for รายการต้องตรวจ (plan §3.3).
 * Owner picks a plain-language answer — never GL debit/credit codes.
 * missing_evidence: แนบ/เปิดหลักฐาน from backend capabilities.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  Eye,
  Loader2,
  Paperclip,
  RefreshCw,
  X,
} from "lucide-react";
import { ModalShell } from "@aera/ui";
import { sanitizeInternalReturnTo } from "@/app/artifacts/table/parseTableUrl";
import { AeraButton } from "@/app/components/ui/AeraButton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/components/ui/DropdownMenu";
import { showToast } from "@/app/components/ui/Toast";
import { billlinAuthUserMessage } from "@/lib/billlinAuthErrors";
import { signBilllinLink } from "@/lib/billlinSignedLinks";
import { createMarketApiFetch } from "@/lib/marketApiFetch";
import { useAuthz } from "@/lib/authz";
import { EvidenceAccessProvider } from "@/lib/authz/EvidenceAccess";

const marketApiFetch = createMarketApiFetch();

function apiDetailText(
  data: { detail?: unknown; error?: unknown },
  status?: number,
): string {
  if (typeof status === "number") {
    const classified = billlinAuthUserMessage(status, data);
    if (classified) return classified;
  }
  const d = data.detail ?? data.error;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d
      .map((x) => (typeof x === "string" ? x : (x as { msg?: string })?.msg || ""))
      .filter(Boolean)
      .join("; ");
  }
  if (d && typeof d === "object" && "msg" in d) {
    return String((d as { msg: unknown }).msg);
  }
  return "";
}

/** Map BE 409 (stale version / already exported) to owner-facing copy. */
function revisionConflictMessage(status: number, detail: string): string | null {
  if (status !== 409) return null;
  const t = detail.toLowerCase();
  if (t.includes("stale version")) {
    return "รายการนี้ถูกอัปเดตจากที่อื่นแล้ว — โหลดรายการล่าสุดให้แล้ว กรุณาเลือกคำตอบใหม่";
  }
  if (t.includes("exported") || t.includes("superseded")) {
    return "รายการนี้ถูกสร้างไฟล์ไปแล้ว — ไม่แก้ทับของเดิม โหลดรายการล่าสุดให้แล้ว";
  }
  return "ข้อมูลขัดแย้งกับเวอร์ชันล่าสุด — โหลดรายการใหม่ให้แล้ว กรุณาลองอีกครั้ง";
}

type HandoffCapabilities = {
  open_evidence?: boolean;
  attach_evidence?: boolean;
  resolve?: boolean;
};

type HandoffItem = {
  handoff_record_id: string;
  period: string;
  source_type: string;
  source_id: string;
  record_type: string;
  occurred_at: string | null;
  amount: string;
  classification_status: string;
  review_reason: string | null;
  review_resolution: string | null;
  counterparty_json: { name?: string } | string | null;
  version: number;
  accounting_status: string;
  has_evidence?: boolean;
  evidence_record_id?: string | null;
  capabilities?: HandoffCapabilities;
};

type ResolutionOption = { code: string; label: string };

const RECORD_TYPE_LABEL: Record<string, string> = {
  customer_payment: "ชำระลูกหนี้",
  unclassified_receipt: "เงินเข้ายังไม่ระบุประเภท",
  customer_credit: "เครดิตลูกค้า",
  advance_receipt: "เงินมัดจำ/รับล่วงหน้า",
  refund_due: "ต้องคืนลูกค้า",
  expense: "รายจ่าย",
  correction: "การแก้ไข",
};

function counterpartyName(raw: HandoffItem["counterparty_json"]): string {
  if (!raw) return "";
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw) as { name?: string };
      return parsed.name || raw;
    } catch {
      return raw;
    }
  }
  return raw.name || "";
}

function formatAmount(raw: string): string {
  const n = Number(raw);
  if (Number.isNaN(n)) return raw;
  return n.toLocaleString("th-TH", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function matchResolutionOption(
  options: ResolutionOption[],
  value: string | null | undefined,
): ResolutionOption | null {
  if (!value) return null;
  return (
    options.find((opt) => opt.code === value || opt.label === value) ?? null
  );
}

function ResolutionSelect({
  options,
  value,
  disabled,
  busy,
  onSave,
}: {
  options: ResolutionOption[];
  value: string | null;
  disabled?: boolean;
  busy?: boolean;
  onSave: (code: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draftCode, setDraftCode] = useState<string | null>(null);
  const saved = matchResolutionOption(options, value);

  useEffect(() => {
    setDraftCode(null);
  }, [value]);

  const draft =
    draftCode != null
      ? (options.find((opt) => opt.code === draftCode) ?? null)
      : null;
  const display = draft ?? saved;
  const dirty = draft != null && draft.code !== saved?.code;

  return (
    <div className="flex items-center gap-1.5">
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <AeraButton
            type="button"
            variant="outline"
            size="xs"
            fontWeight="semibold"
            disabled={disabled || busy}
            className="h-8 w-[8.5rem] justify-between px-2.5!"
            aria-label={
              display ? `คำตอบ: ${display.label}` : "เลือกคำตอบ"
            }
            aria-expanded={open}
            iconRight={
              busy ? (
                <Loader2 size={14} className="animate-spin text-aera-gray" />
              ) : (
                <ChevronDown
                  size={14}
                  className={`shrink-0 text-aera-gray transition-transform duration-200 ${
                    open ? "rotate-180" : "rotate-0"
                  }`}
                />
              )
            }
          >
            <span className="min-w-0 flex-1 truncate text-left">
              {display?.label || "เลือกคำตอบ"}
            </span>
          </AeraButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={6}
          className="z-[10100] min-w-[14rem] rounded-xl border border-aera-lightgray/80 p-1.5 shadow-[0_10px_30px_rgba(0,0,0,0.08)]"
        >
          {options.map((opt) => {
            const active = opt.code === (draft?.code ?? saved?.code);
            return (
              <DropdownMenuItem
                key={opt.code}
                disabled={busy}
                onClick={() => {
                  setOpen(false);
                  setDraftCode(opt.code);
                }}
                className="cursor-pointer justify-between gap-3 rounded-lg px-2.5 py-2 text-xs font-semibold text-aera-charcoal"
              >
                <span>{opt.label}</span>
                {active ? (
                  <Check
                    className="h-3.5 w-3.5 shrink-0 text-aera-amber"
                    strokeWidth={3}
                    aria-hidden
                  />
                ) : (
                  <span className="h-3.5 w-3.5 shrink-0" aria-hidden />
                )}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      {dirty ? (
        <AeraButton
          type="button"
          variant="primary"
          size="xs"
          fontWeight="semibold"
          disabled={busy}
          loading={busy}
          className="h-8 shrink-0 px-2.5!"
          onClick={() => {
            if (draftCode) onSave(draftCode);
          }}
        >
          บันทึก
        </AeraButton>
      ) : null}
    </div>
  );
}

export type AccountingReviewContentProps = {
  workspaceId: string;
  serviceId?: string;
  period: string;
  classification?: string;
  returnTo?: string;
  /** When set, back closes the host modal instead of navigating away. */
  onClose?: () => void;
  /** Fill parent height (modal) instead of the viewport (page). */
  embedded?: boolean;
};

export function AccountingReviewContent({
  workspaceId,
  serviceId = "",
  period,
  classification: classificationProp = "needs_review",
  returnTo: returnToRaw = "",
  onClose,
  embedded = false,
}: AccountingReviewContentProps) {
  const classification = classificationProp.trim() || "needs_review";
  const { can: canAuthz, ready: authzReady } = useAuthz(workspaceId);
  const canReadEvidence =
    authzReady &&
    canAuthz(
      "evidence",
      "read",
      "เปิดหลักฐาน handoff ในหน้าตรวจส่งบัญชี",
    );

  const [items, setItems] = useState<HandoffItem[] | null>(null);
  const [options, setOptions] = useState<ResolutionOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [evidencePreviewOpen, setEvidencePreviewOpen] = useState(false);
  const [evidencePreview, setEvidencePreview] = useState<{
    handoffId: string;
    url: string;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const attachTargetRef = useRef<HandoffItem | null>(null);

  const closeEvidencePreview = useCallback(() => {
    setEvidencePreviewOpen(false);
  }, []);

  const backHref = useMemo(() => {
    const p = new URLSearchParams();
    if (workspaceId) p.set("workspaceId", workspaceId);
    if (serviceId) p.set("serviceId", serviceId);
    // Keep origin page so back from ส่งบัญชี still returns correctly.
    const safeReturn = sanitizeInternalReturnTo(returnToRaw, "");
    if (safeReturn) p.set("returnTo", safeReturn);
    return `/artifacts/accounting?${p.toString()}`;
  }, [workspaceId, serviceId, returnToRaw]);

  const title =
    classification === "ready"
      ? "รายการพร้อมส่ง"
      : classification === "missing_evidence"
        ? "เอกสารขาด"
        : "รายการต้องตรวจ";

  const load = useCallback(async () => {
    if (!workspaceId || !period) return;
    setLoading(true);
    setError(null);
    try {
      // พร้อมส่ง = draft queue only. ต้องตรวจ/เอกสารขาด = latest non-superseded
      // (รวมที่เคย exported แล้วแต่ยังค้าง) ให้ตรงกับตัวเลขในตารางส่งบัญชี
      const acctFilter =
        classification === "ready" ? "not_prepared" : "";
      const [handoffsRes, optsRes] = await Promise.all([
        marketApiFetch(
          `/api/v1/accounting/periods/${encodeURIComponent(period)}/handoffs?workspace_id=${encodeURIComponent(workspaceId)}&classification_status=${encodeURIComponent(classification)}&accounting_status=${encodeURIComponent(acctFilter)}`,
          { cache: "no-store" },
        ),
        marketApiFetch(`/api/v1/accounting/handoffs/resolution-options`, { cache: "no-store" }),
      ]);
      if (!handoffsRes.ok) {
        const data = await handoffsRes.json().catch(() => ({}));
        throw new Error(
          apiDetailText(data, handoffsRes.status) ||
            `โหลดรายการไม่สำเร็จ (${handoffsRes.status})`,
        );
      }
      const handoffsData = await handoffsRes.json();
      const raw = Array.isArray(handoffsData.items) ? handoffsData.items : [];
      const items =
        classification === "ready"
          ? raw
          : raw.filter((x: HandoffItem) => x.accounting_status !== "superseded");
      setItems(items);
      if (optsRes.ok) {
        const optsData = await optsRes.json();
        setOptions(Array.isArray(optsData.options) ? optsData.options : []);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "โหลดไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  }, [classification, workspaceId, period]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleResolve = async (item: HandoffItem, code: string) => {
    if (!workspaceId || !code) return;
    setBusyId(item.handoff_record_id);
    try {
      const res = await marketApiFetch(
        `/api/v1/accounting/handoffs/${encodeURIComponent(item.handoff_record_id)}/resolve`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            workspace_id: workspaceId,
            resolution: code,
            expected_version: item.version,
          }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = apiDetailText(data, res.status);
        const conflict = revisionConflictMessage(res.status, detail);
        if (conflict) {
          // FE-3: refresh with latest versions so the next resolve uses a fresh expected_version.
          await load();
          showToast(conflict, "error", undefined, 5500);
          return;
        }
        throw new Error(detail || `บันทึกไม่สำเร็จ (${res.status})`);
      }
      showToast(`บันทึกคำตอบแล้ว: ${data.review_resolution || code}`);
      await load();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "บันทึกไม่สำเร็จ", "error");
    } finally {
      setBusyId(null);
    }
  };

  const openEvidence = (item: HandoffItem) => {
    if (!workspaceId) return;
    if (!canReadEvidence) {
      showToast("บทบาทของคุณดูหลักฐานไม่ได้", "error");
      return;
    }
    const path = `/api/v1/accounting/handoffs/${encodeURIComponent(item.handoff_record_id)}/evidence?workspace_id=${encodeURIComponent(workspaceId)}`;
    void (async () => {
      try {
        const signed = await signBilllinLink(path);
        setEvidencePreview({ handoffId: item.handoff_record_id, url: signed });
        setEvidencePreviewOpen(true);
      } catch (err) {
        showToast(
          err instanceof Error ? err.message : "เปิดหลักฐานไม่สำเร็จ",
          "error",
        );
      }
    })();
  };

  const startAttach = (item: HandoffItem) => {
    attachTargetRef.current = item;
    fileInputRef.current?.click();
  };

  const handleAttachFile = async (file: File | null) => {
    const item = attachTargetRef.current;
    attachTargetRef.current = null;
    if (!file || !item || !workspaceId) return;
    setBusyId(item.handoff_record_id);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await marketApiFetch(
        `/api/v1/accounting/handoffs/${encodeURIComponent(item.handoff_record_id)}/attach-evidence?workspace_id=${encodeURIComponent(workspaceId)}`,
        { method: "POST", body: form },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          apiDetailText(data, res.status) || `แนบเอกสารไม่สำเร็จ (${res.status})`,
        );
      }
      showToast(
        data.classification_status === "ready"
          ? "แนบเอกสารแล้ว — รายการพร้อมส่ง"
          : "แนบเอกสารแล้ว",
        "success",
      );
      await load();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "แนบเอกสารไม่สำเร็จ", "error");
    } finally {
      setBusyId(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const backButton = onClose ? (
    <AeraButton
      type="button"
      variant="ghost"
      size="sm"
      paddingSize="none"
      onClick={onClose}
      className="flex! h-9! w-9! shrink-0 items-center justify-center rounded-xl! bg-aera-offwhite text-aera-gray hover:text-aera-charcoal"
      aria-label="ปิด"
      icon={<ArrowLeft size={18} />}
    />
  ) : (
    <AeraButton
      href={backHref}
      variant="ghost"
      size="sm"
      paddingSize="none"
      className="flex! h-9! w-9! shrink-0 items-center justify-center rounded-xl! bg-aera-offwhite text-aera-gray hover:text-aera-charcoal"
      aria-label="กลับรอบบัญชี"
      icon={<ArrowLeft size={18} />}
    />
  );

  const refreshButton = (
    <AeraButton
      type="button"
      variant="outline"
      size="xs"
      fontWeight="semibold"
      onClick={() => void load()}
      disabled={loading}
      className={embedded ? "shrink-0" : "ml-auto shrink-0"}
      icon={
        <RefreshCw
          size={14}
          className={loading ? "animate-spin" : undefined}
        />
      }
    >
      รีเฟรช
    </AeraButton>
  );

  return (
    <EvidenceAccessProvider workspaceId={workspaceId}>
    <div
      className={`flex flex-col text-aera-charcoal [&_a]:cursor-pointer [&_button:not(:disabled)]:cursor-pointer [&_button:disabled]:cursor-not-allowed ${
        embedded
          ? "h-full min-h-0 bg-aera-white"
          : "h-dvh min-h-0 bg-aera-offwhite"
      }`}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={(e) => void handleAttachFile(e.target.files?.[0] ?? null)}
      />

      {embedded ? (
        <div className="mb-2 flex shrink-0 justify-end">{refreshButton}</div>
      ) : (
        <div className="flex items-center gap-3 border-b border-aera-lightgray bg-aera-white p-3 md:p-4">
          {backButton}
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{title}</p>
            <p className="truncate text-xs text-aera-gray">รอบ {period || "—"}</p>
          </div>
          {refreshButton}
        </div>
      )}

      <ModalShell
        isOpen={evidencePreviewOpen}
        onClose={closeEvidencePreview}
        onExited={() => setEvidencePreview(null)}
        align="center"
        zIndexClass="z-[10050]"
        panelClassName="!mx-auto !w-[min(480px,calc(100vw-2rem))] max-w-[480px] rounded-2xl border border-aera-lightgray bg-aera-white p-3 shadow-xl [&_a]:cursor-pointer [&_button:not(:disabled)]:cursor-pointer [&_button:disabled]:cursor-not-allowed"
      >
        {evidencePreview ? (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold text-aera-charcoal">หลักฐาน</p>
              <AeraButton
                type="button"
                variant="ghost"
                size="xxs"
                paddingSize="xxs"
                onClick={closeEvidencePreview}
                className="text-aera-gray hover:bg-aera-offwhite hover:text-aera-charcoal"
                aria-label="ปิด"
                icon={<X size={16} />}
              />
            </div>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={evidencePreview.url}
              alt="หลักฐาน"
              className="max-h-[70vh] w-full rounded-xl object-contain bg-aera-offwhite"
            />
          </div>
        ) : null}
      </ModalShell>

      <div
        className={
          embedded
            ? "flex min-h-0 flex-1 flex-col pb-1"
            : "flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain p-3 md:p-4"
        }
      >
        {!workspaceId || !period ? (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-3 py-18 text-center">
            <h3 className="mb-1 text-lg font-bold text-aera-charcoal">
              เปิดหน้านี้จากรอบบัญชี
            </h3>
            <p className="max-w-[400px] text-sm leading-relaxed text-aera-gray">
              กดปุ่มเปิดตรวจจากตารางรอบบัญชีเพื่อดูรายการ
            </p>
          </div>
        ) : error ? (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-3 py-18 text-center">
            <h3 className="mb-1 text-lg font-bold text-aera-charcoal">
              โหลดรายการไม่สำเร็จ
            </h3>
            <p className="mb-5 max-w-[400px] text-sm leading-relaxed text-aera-red">
              {error}
            </p>
            <AeraButton
              variant="primary"
              fontWeight="medium"
              size="sm"
              onClick={() => void load()}
            >
              ลองใหม่
            </AeraButton>
          </div>
        ) : loading && items === null ? (
          <div
            role="status"
            aria-live="polite"
            aria-busy="true"
            className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 py-18 text-aera-gray"
          >
            <Loader2 className="h-8 w-8 animate-spin text-aera-amber" aria-hidden />
            <p className="text-sm">กำลังโหลด…</p>
          </div>
        ) : !items || items.length === 0 ? (
          embedded ? (
            <p className="rounded-xl border border-dashed border-aera-lightgray px-4 py-8 text-center text-sm text-aera-gray">
              {classification === "ready"
                ? "ยังไม่มีรายการพร้อมส่ง"
                : classification === "missing_evidence"
                  ? "ไม่มีเอกสารขาด"
                  : "ไม่มีรายการต้องตรวจ"}
            </p>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-3 py-18 text-center">
              <span className="mb-3 text-5xl opacity-50 grayscale" aria-hidden>
                {classification === "ready"
                  ? "📤"
                  : classification === "missing_evidence"
                    ? "📎"
                    : "✅"}
              </span>
              <h3 className="mb-5 text-lg font-bold text-aera-charcoal">
                {classification === "ready"
                  ? "ยังไม่มีรายการพร้อมส่ง"
                  : classification === "missing_evidence"
                    ? "ไม่มีเอกสารขาด"
                    : "ไม่มีรายการต้องตรวจ"}
              </h3>
              <AeraButton
                href={backHref}
                variant="primary"
                fontWeight="medium"
                size="sm"
              >
                กลับรอบบัญชี
              </AeraButton>
            </div>
          )
        ) : (
          <div
            className={
              embedded
                ? "min-h-0 flex-1 overflow-auto overscroll-contain rounded-2xl border border-aera-lightgray bg-aera-white"
                : "overflow-hidden rounded-2xl border border-aera-lightgray bg-aera-white"
            }
          >
            <div className={embedded ? undefined : "overflow-x-auto"}>
              <table className="w-full min-w-[780px] text-sm">
                <thead className="sticky top-0 z-10">
                  <tr className="border-b border-aera-lightgray bg-aera-white text-left text-xs text-aera-gray">
                    <th className="whitespace-nowrap bg-aera-white px-4 py-3 font-semibold">
                      วันเวลา
                    </th>
                    <th className="whitespace-nowrap bg-aera-white px-4 py-3 font-semibold">
                      รายการ
                    </th>
                    <th className="whitespace-nowrap bg-aera-white px-4 py-3 font-semibold">
                      ปัญหา
                    </th>
                    <th className="whitespace-nowrap bg-aera-white px-4 py-3 text-right font-semibold">
                      ยอด
                    </th>
                    <th className="whitespace-nowrap bg-aera-white px-4 py-3 font-semibold">
                      {classification === "needs_review"
                        ? "เลือกคำตอบ"
                        : classification === "missing_evidence"
                          ? "หลักฐาน"
                          : "รายละเอียด"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const name = counterpartyName(item.counterparty_json);
                    const typeLabel =
                      RECORD_TYPE_LABEL[item.record_type] || item.record_type;
                    const busy = busyId === item.handoff_record_id;
                    const caps = item.capabilities || {};
                    const canOpen =
                      caps.open_evidence ?? Boolean(item.has_evidence);
                    const canAttach =
                      caps.attach_evidence ??
                      (classification === "missing_evidence" &&
                        item.source_type === "expense");
                    const canResolve =
                      caps.resolve ?? classification === "needs_review";
                    return (
                      <tr
                        key={item.handoff_record_id}
                        className="border-b border-aera-lightgray align-middle last:border-b-0"
                      >
                        <td className="whitespace-nowrap px-4 py-3 text-xs tabular-nums text-aera-gray">
                          {item.occurred_at?.slice(0, 16).replace("T", " ") || "—"}
                        </td>
                        <td className="px-4 py-3 font-semibold text-aera-charcoal">
                          {typeLabel}
                          {name ? ` · ${name}` : ""}
                        </td>
                        <td className="px-4 py-3">
                          {item.review_reason ? (
                            <span className="text-xs font-semibold text-aera-amber">
                              {item.review_reason}
                            </span>
                          ) : (
                            <span className="text-xs text-aera-gray">—</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right font-semibold tabular-nums">
                          {formatAmount(item.amount)} บาท
                        </td>
                        <td className="px-4 py-3">
                          {canResolve ? (
                            <ResolutionSelect
                              options={options}
                              value={item.review_resolution}
                              disabled={item.accounting_status === "exported"}
                              busy={busy}
                              onSave={(code) => void handleResolve(item, code)}
                            />
                          ) : classification === "missing_evidence" ? (
                            <div className="flex flex-wrap items-center gap-1.5">
                              {canOpen ? (
                                <AeraButton
                                  type="button"
                                  variant="outline"
                                  size="xxs"
                                  fontWeight="semibold"
                                  disabled={busy}
                                  onClick={() => openEvidence(item)}
                                  className="whitespace-nowrap"
                                  icon={<Eye size={12} />}
                                >
                                  เปิดหลักฐาน
                                </AeraButton>
                              ) : null}
                              {canAttach ? (
                                <AeraButton
                                  type="button"
                                  variant="secondary"
                                  size="xxs"
                                  fontWeight="semibold"
                                  disabled={busy}
                                  onClick={() => startAttach(item)}
                                  className="whitespace-nowrap border-aera-green! bg-aera-green! text-aera-white! hover:opacity-90"
                                  icon={
                                    busy ? (
                                      <Loader2
                                        size={12}
                                        className="animate-spin"
                                      />
                                    ) : (
                                      <Paperclip size={12} />
                                    )
                                  }
                                >
                                  แนบเอกสาร
                                </AeraButton>
                              ) : (
                                <span className="text-xs text-aera-gray">
                                  รอเอกสาร
                                </span>
                              )}
                            </div>
                          ) : item.review_resolution ? (
                            <span className="text-xs font-semibold text-aera-green">
                              {item.review_resolution}
                            </span>
                          ) : (
                            <div className="flex flex-wrap items-center gap-1.5">
                              {canOpen ? (
                                <AeraButton
                                  type="button"
                                  variant="outline"
                                  size="xxs"
                                  fontWeight="semibold"
                                  onClick={() => openEvidence(item)}
                                  className="whitespace-nowrap"
                                  icon={<Eye size={12} />}
                                >
                                  เปิดหลักฐาน
                                </AeraButton>
                              ) : (
                                <span className="text-xs text-aera-gray">
                                  พร้อมส่ง
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
    </EvidenceAccessProvider>
  );
}

/** Page entry — reads workspace/period from the URL search params. */
export default function AccountingReviewShell() {
  const searchParams = useSearchParams();
  const workspaceId = searchParams.get("workspaceId")?.trim() ?? "";
  const serviceId = searchParams.get("serviceId")?.trim() ?? "";
  const period = searchParams.get("period")?.trim() ?? "";
  const classification =
    searchParams.get("classification")?.trim() || "needs_review";
  const returnTo = searchParams.get("returnTo")?.trim() ?? "";

  return (
    <AccountingReviewContent
      workspaceId={workspaceId}
      serviceId={serviceId}
      period={period}
      classification={classification}
      returnTo={returnTo}
    />
  );
}
